// app/page.js (Halaman Utama - Fokus karya_veo)
"use client";

import { useState, useEffect, useCallback } from "react";
import { db, auth } from "@/lib/firebaseClient";
import { signInAnonymously, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  query,
  orderBy,
  arrayUnion,
  increment,
} from "firebase/firestore";
import KaryaCard from "./components/KaryaCard";
import MediaModal from "./components/MediaModal";

export const runtime = "edge";

export default function Home() {
  const [karyaList, setKaryaList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedKarya, setSelectedKarya] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("votes");
  const [isVoting, setIsVoting] = useState(false);

  const openModal = useCallback((karya) => setSelectedKarya(karya), []);
  const closeModal = useCallback(() => setSelectedKarya(null), []);

  // Fungsi untuk mengurutkan daftar karya di client-side
  const sortKaryaList = useCallback((list, sortType) => {
    const sorted = [...list];
    switch (sortType) {
      case "newest":
        return sorted.sort((a, b) => (b.createdAt?.toDate() || 0) - (a.createdAt?.toDate() || 0));
      case "oldest":
        return sorted.sort((a, b) => (a.createdAt?.toDate() || 0) - (b.createdAt?.toDate() || 0));
      case "title":
        return sorted.sort((a, b) => a.title.localeCompare(b.title));
      case "votes":
      default:
        return sorted.sort((a, b) => (b.voteCount || 0) - (a.voteCount || 0));
    }
  }, []);

  const fetchKarya = useCallback(async () => {
    setIsLoading(true);
    try {
      // HANYA ambil dari karya_veo
      const karyaVeoQuery = query(collection(db, "karya_veo"));
      const karyaVeoSnapshot = await getDocs(karyaVeoQuery);

      const fetchedKaryaVeo = karyaVeoSnapshot.docs.map((doc) => ({
        id: doc.id,
        collectionName: 'karya_veo', // Tetap kasih identifier
        ...doc.data(),
      }));

      const sortedKarya = sortKaryaList(fetchedKaryaVeo, sortBy);
      setKaryaList(sortedKarya);

    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [sortBy, sortKaryaList]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
        fetchKarya();
      } else {
        signInAnonymously(auth).catch((error) => {
          console.error("Error signing in anonymously:", error);
        });
      }
    });

    return () => unsubscribe();
  }, [fetchKarya]);

  const handleVote = async (karyaToVote) => {
    if (!currentUser || isVoting) return;
    
    if (karyaToVote.voters?.includes(currentUser.uid)) {
      return;
    }

    setIsVoting(true);
    
    // Optimistic UI update
    const originalList = [...karyaList];
    const updatedList = karyaList.map((karya) => {
      if (karya.id === karyaToVote.id) {
        return {
          ...karya,
          voteCount: (karya.voteCount || 0) + 1,
          voters: [...(karya.voters || []), currentUser.uid],
        };
      }
      return karya;
    });
    
    setKaryaList(sortKaryaList(updatedList, sortBy));

    try {
      // Selalu update di karya_veo
      const karyaRef = doc(db, "karya_veo", karyaToVote.id);
      await updateDoc(karyaRef, {
        voters: arrayUnion(currentUser.uid),
        voteCount: increment(1),
      });
    } catch (error) {
      console.error("Error updating vote:", error);
      setKaryaList(originalList);
    } finally {
      setIsVoting(false);
    }
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };

  // Filter pencarian yang mendukung author sebagai array
  const filteredKaryaList = karyaList.filter(
    (karya) =>
      karya.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (Array.isArray(karya.author) &&
        karya.author.some(name => name.toLowerCase().includes(searchTerm.toLowerCase())))
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-slate-900 dark:to-purple-900">
      <nav className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-lg border-b border-gray-200/50 dark:border-gray-800/50 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">VG</span>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                VideoGallery
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <a 
                href="/" 
                className="text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 font-medium transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20"
              >
                Gallery
              </a>
              <a 
                href="/upload" 
                className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-5 py-2.5 rounded-xl hover:from-purple-600 hover:to-blue-600 transition-all duration-300 font-medium shadow-lg hover:shadow-purple-500/25 transform hover:-translate-y-0.5"
              >
                Upload Video
              </a>
            </div>
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-4 py-8">
        <header className="text-center mb-16 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></span>
              <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
                Platform Video Kreatif Terdepan
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Temukan{" "}
              <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                Video Menakjubkan
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed max-w-2xl mx-auto">
              Jelajahi galeri video kreatif dari konten kreator berbakat. Vote
              video favoritmu dan bagikan apresiasimu kepada dunia.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a 
                href="/upload" 
                className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-8 py-4 rounded-xl hover:from-purple-600 hover:to-blue-600 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Upload Video
              </a>
              <a 
                href="#gallery" 
                className="border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-8 py-4 rounded-xl hover:border-purple-500 hover:text-purple-600 dark:hover:text-purple-400 transition-all duration-300 font-semibold hover:bg-white dark:hover:bg-gray-800"
              >
                Jelajahi Video
              </a>
            </div>
          </div>
        </header>

        <section id="gallery" className="mb-20">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-6">
            <div className="flex-1">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Koleksi Video
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                {filteredKaryaList.length} video ditemukan
                {searchTerm && (
                  <span className="text-purple-600 dark:text-purple-400 ml-2">
                    untuk "{searchTerm}"
                  </span>
                )}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                  </svg>
                </div>
                <input 
                  type="text" 
                  placeholder="Cari video atau kreator..." 
                  value={searchTerm} 
                  onChange={(e) => setSearchTerm(e.target.value)} 
                  className="pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent w-full sm:w-64 transition-all duration-200"
                />
              </div>
              <select 
                value={sortBy} 
                onChange={handleSortChange} 
                className="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
              >
                <option value="votes">Paling Banyak Vote</option>
                <option value="newest">Video Terbaru</option>
                <option value="oldest">Video Terlama</option>
                <option value="title">Judul (A-Z)</option>
              </select>
            </div>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-4 animate-pulse">
                  <div className="bg-gray-300 dark:bg-gray-700 rounded-xl h-48 mb-4"></div>
                  <div className="bg-gray-300 dark:bg-gray-700 rounded h-4 mb-2 w-3/4"></div>
                  <div className="bg-gray-300 dark:bg-gray-700 rounded h-3 w-1/2"></div>
                  <div className="flex justify-between items-center mt-4">
                    <div className="bg-gray-300 dark:bg-gray-700 rounded h-4 w-16"></div>
                    <div className="bg-gray-300 dark:bg-gray-700 rounded-lg h-8 w-16"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredKaryaList.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-32 h-32 mx-auto mb-6 text-gray-400 dark:text-gray-600">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-3">
                Tidak ada video ditemukan
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
                {searchTerm ? 
                  `Tidak ada hasil untuk "${searchTerm}". Coba ubah kata kunci pencarian.` : 
                  'Belum ada video yang diupload. Jadilah yang pertama!'
                }
              </p>
              <a 
                href="/upload" 
                className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-8 py-3 rounded-xl hover:from-purple-600 hover:to-blue-600 transition-all duration-300 font-medium inline-flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Upload Video Pertama
              </a>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredKaryaList.map((karya) => (
                <KaryaCard
                  key={karya.id}
                  karya={karya}
                  onVote={handleVote}
                  hasVoted={karya.voters?.includes(currentUser?.uid)}
                  onMediaClick={openModal}
                  isVoting={isVoting}
                />
              ))}
            </div>
          )}
        </section>

        {selectedKarya && (
          <MediaModal karya={selectedKarya} onClose={closeModal} />
        )}
      </main>

      <footer className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <div className="w-6 h-6 bg-gradient-to-r from-purple-600 to-blue-600 rounded flex items-center justify-center">
                <span className="text-white font-bold text-xs">VG</span>
              </div>
              <span className="text-lg font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                VideoGallery
              </span>
            </div>
            <div className="text-gray-600 dark:text-gray-400 text-center md:text-right">
              <p className="text-sm">&copy; 2025 VideoGallery. All rights reserved.</p>
              <p className="text-xs mt-1 opacity-75">
                Platform berbagi video kreatif untuk semua konten kreator
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}